/* eslint-disable max-len */
import React from 'react';
import classNames from 'classnames';
import _ from 'underscore';
import type { I18nModel, ICurrencyOptions, ICurrencyProps, ICurrencyTextOptions } from './types';

const placeholder = '-';

function getCurrencyTextWithoutCurrencyCode(
  currencyCode: string | undefined,
  amount: number | string | undefined,
  i18n: I18nModel,
  maxFractionDigits: number
): string {
  const currencySymbol = i18n.getCurrencySymbol(currencyCode);
  return _.isFinite(amount) && currencySymbol
    ? i18n.formatCurrency(
      amount as number,
      currencyCode,
      { maximumFractionDigits: Math.max(maxFractionDigits, 0) } as ICurrencyOptions
    )
    : `${currencySymbol || ''} ${_.isString(amount) ? amount : placeholder}`;
}

/**
 * Gets Currency as text
 * @property {String} currencyCode - the currency code: https://www.iso.org/iso-4217-currency-codes.html
 * @property {Number | String} amount - the money amount, when amount is string will return amount string with currency
 * @property {Object} i18n - i18n object which offers localized formatter
 * @property {Number} maxFractionDigits - non-negative integer for the maximum decimal digits
 * @property {Boolean} hideCurrencyCode - Whether to show the currency code after the value
 * @property {String} separator - The separator to join with between amount and currency code
 */

export function getCurrencyText({
  currencyCode,
  amount,
  i18n,
  maxFractionDigits = 2,
  hideCurrencyCode = false,
  separator = '',
}: ICurrencyTextOptions): string {
  const currencyTextWithoutCurrencyCode = getCurrencyTextWithoutCurrencyCode(
    currencyCode,
    amount,
    i18n,
    maxFractionDigits
  );
  return hideCurrencyCode || !_.isString(currencyCode)
    ? currencyTextWithoutCurrencyCode
    : `${currencyTextWithoutCurrencyCode}${separator}${currencyCode}`;
}

/**
 * Currency Props
 * @property {String} name - name of the currency component
 * @property {Array} containerClass - list of class names to decorate the container
 * @property {Object} i18n - i18n object which offers localized formatter
 * @property {String} currencyCode - the currency code: https://www.iso.org/iso-4217-currency-codes.html
 * @property {Array} currencyClass - list of class names to decorate the currency part
 * @property {Number} amount - the money amount
 * @property {Array} amountClass - list of class names to decorate the amount part
 * @property {Number} maxFractionDigits - non-negative integer for the maximum decimal digits
 * @property {Boolean} hideCurrencyCode - Whether to show the currency code after the value
 */

export function Currency({
  name,
  containerClass,
  i18n,
  currencyCode,
  currencyClass,
  amount,
  amountClass,
  maxFractionDigits,
  hideCurrencyCode,
}: ICurrencyProps) {
  const formatterClassName = `currency-${name || _.uniqueId()}`;
  const amountWithCurrency = getCurrencyTextWithoutCurrencyCode(
    currencyCode,
    amount,
    i18n,
    maxFractionDigits as number
  );

  return (
    <div className={classNames(formatterClassName, containerClass)}>
      <span className={classNames('currency-amount', amountClass)}>{amountWithCurrency}</span>
      {!hideCurrencyCode && <span className={classNames('currency-code', currencyClass)}>{currencyCode}</span>}
    </div>
  );
}

Currency.defaultProps = {
  name: '',
  containerClass: [],
  amount: undefined,
  currencyCode: undefined,
  currencyClass: [],
  amountClass: [],
  maxFractionDigits: 2,
  hideCurrencyCode: false,
};
